import { getApiBaseUrl, getToken } from "./utils"

// src/api/templates.js

const API_URL = `${getApiBaseUrl()}/templates`;

const DEFAULT_TEMPLATE_JSON = {
  width: 1080,
  height: 1920,
  fonts: [],
  unit: "px",
  dpi: 72,
  pages: [
    {
      id: "page-1",
      children: [],
      width: "auto",
      height: "auto",
      background: "rgba(255,255,255,1)",
      bleed: 0,
      duration: 5000,
    },
  ],
};

async function buildApiError(response, fallbackMessage) {
  let message = fallbackMessage;

  try {
    const payload = await response.json();
    const detail = payload?.detail ?? payload?.message ?? payload;

    if (Array.isArray(detail)) {
      message = detail
        .map((item) => {
          if (typeof item === "string") return item;
          const path = Array.isArray(item?.loc) ? item.loc.join(" > ") : "";
          const reason = item?.msg || item?.message || JSON.stringify(item);
          return path ? `${path}: ${reason}` : reason;
        })
        .join("\n");
    } else if (typeof detail === "object" && detail !== null) {
      message = detail?.msg || detail?.message || JSON.stringify(detail);
    } else if (detail) {
      message = String(detail);
    }
  } catch (_error) {
    try {
      const text = await response.text();
      if (text) {
        message = text;
      }
    } catch (_textError) {
      // ignore
    }
  }

  return new Error(message);
}

export async function getTemplates(options = {}) {
  const params = new URLSearchParams();
  params.set("skip", options.skip ?? 0);
  params.set("limit", options.limit ?? 50);

  if (options.category) {
    params.set("category", options.category);
  }
  if (options.language) {
    params.set("language", options.language);
  }
  if (options.culture) {
    params.set("culture", options.culture);
  }
  if (options.eventType) {
    params.set("event_type", options.eventType);
  }

  const response = await fetch(`${API_URL}/templates/?${params.toString()}`);
  if (!response.ok) throw new Error("Failed to load templates");
  return await response.json();
}

export async function getTemplateById(id) {
  const response = await fetch(`${API_URL}/templates/${id}`);
  if (!response.ok) throw new Error("Failed to load template");
  return await response.json();
}

function buildTemplatePayload({ template, htmltext, thumbnail }) {
  const inferredEventType =
    template?.event_type ||
    (template?.category ? `${template.category}`.trim().toLowerCase().replace(/\s+/g, "_") : null);

  return {
    name: template?.name ?? "Untitled Template",
    category: template?.category ?? null,
    faith_or_religion: template?.faith_or_religion ?? null,
    culture: template?.culture ?? null,
    language: template?.language ?? null,
    event_type: inferredEventType,
    subevent_types: template?.subevent_types ?? [],
    htmltext,
    color_combinations: template?.color_combinations ?? null,
    tags: template?.tags ?? [],
    style_tags: template?.style_tags ?? [],
    culture_tags: template?.culture_tags ?? (template?.culture ? [template.culture] : []),
    religion_tags:
      template?.religion_tags ??
      (template?.faith_or_religion ? [template.faith_or_religion] : []),
    language_tags:
      template?.language_tags ?? (template?.language ? [template.language] : []),
    thumbnail_image: thumbnail ?? template?.thumbnail_image ?? null,
    background_image: template?.background_image ?? null,
    preview_video_url: template?.preview_video_url ?? null,
    is_featured: Boolean(template?.is_featured),
    is_premium: Boolean(template?.is_premium),
    subtemplates: template?.subtemplates ?? [],
    prices: template?.prices ?? [],
    discounts: template?.discounts ?? [],
  };
}

export async function saveTemplate({ template, json, thumbnail }) {
  const isCreate = !template?.id;
  const method = isCreate ? "POST" : "PUT";
  const api = isCreate
    ? `${API_URL}/create`
    : `${API_URL}/templates/${template.id}`;

  const payload = buildTemplatePayload({
    template,
    htmltext: JSON.stringify(json),
    thumbnail,
  });

  if (!isCreate) {
    payload.id = template.id;
  }

  const response = await fetch(api, {
    method: method,
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw await buildApiError(response, "Failed to save template");
  return await response.json();
}

export async function updateTemplateMetadata(template) {
  if (!template?.id) {
    throw new Error("Template id is required to update metadata.");
  }

  const payload = buildTemplatePayload({
    template,
    htmltext: template?.htmltext ?? "",
    thumbnail: template?.thumbnail_image ?? null,
  });
  payload.id = template.id;

  const response = await fetch(`${API_URL}/templates/${template.id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw await buildApiError(response, "Failed to update template metadata");
  return await response.json();
}

export async function createTemplateMetadata(template) {
  const payload = buildTemplatePayload({
    template,
    htmltext: template?.htmltext || JSON.stringify(DEFAULT_TEMPLATE_JSON),
    thumbnail: template?.thumbnail_image ?? null,
  });

  const response = await fetch(`${API_URL}/create`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw await buildApiError(response, "Failed to create template");
  return await response.json();
}

export async function deleteTemplate(id) {
  const response = await fetch(`${API_URL}/templates/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to delete template");
  return await response.json();
}
